import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { Lock, LogIn, Mail, ShieldCheck } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';

// Layout
import { DashboardLayout } from './components/layout';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import Imobiliaria from './pages/Imobiliaria/Imobiliaria';
import Construtora from './pages/Construtora/Construtora';
import RH from './pages/RH/RH';
import Frota from './pages/Frota/Frota';
import Estoque from './pages/Estoque/Estoque';
import Chat from './pages/Chat/Chat';
import Marketing from './pages/Marketing/Marketing';
import NexusTeam from './pages/NexusTeam/NexusTeam';
import Predictive from './pages/Predictive/Predictive';
import Settings from './pages/Settings/Settings';
import Juridico from './pages/Juridico/Juridico';
import DocumentosOCR from './pages/Documentos/DocumentosOCR';
import RevisoesPendentes from './pages/Documentos/RevisoesPendentes';

// Financeiro Pages
import FinanceiroDashboard from './pages/Financeiro/FinanceiroDashboard';
import FinanceiroTransacoes from './pages/Financeiro/FinanceiroTransacoes';
import FinanceiroFluxoCaixa from './pages/Financeiro/FinanceiroFluxoCaixa';
import FinanceiroDRE from './pages/Financeiro/FinanceiroDRE';
import FinanceiroConsolidado from './pages/Financeiro/FinanceiroConsolidado';
import FinanceiroContasPagar from './pages/Financeiro/FinanceiroContasPagar';
import FinanceiroContasReceber from './pages/Financeiro/FinanceiroContasReceber';
import FinanceiroConciliacao from './pages/Financeiro/FinanceiroConciliacao';
import FinanceiroCartoes from './pages/Financeiro/FinanceiroCartoes';

// Styles
import './styles/global.css';

// React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children, permission }) => {
  const token = localStorage.getItem('nexus_token');
  const location = useLocation();
  const { user, loading, hasPermission } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-nexus-darker flex items-center justify-center text-white/60">
        Carregando acesso...
      </div>
    );
  }

  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (permission && !hasPermission(permission)) {
    return (
      <div className="min-h-screen bg-nexus-darker flex items-center justify-center p-6">
        <div className="max-w-md rounded-xl border border-white/10 bg-slate-900/70 p-6 text-center">
          <h1 className="text-xl font-semibold text-white/90">Acesso restrito</h1>
          <p className="mt-2 text-sm text-white/50">Seu perfil não possui permissão para abrir esta área.</p>
        </div>
      </div>
    );
  }

  return children;
};

const Guarded = ({ permission, children }) => (
  <ProtectedRoute permission={permission}>{children}</ProtectedRoute>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, login, completeMfaLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [loading, user, from, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (result.success) {
      navigate(from, { replace: true });
      return;
    }
    if (result.mfaRequired) {
      setMfaChallenge(result.challenge);
      return;
    }
    setError(result.error || 'Não foi possível entrar.');
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await completeMfaLogin(mfaChallenge, mfaCode);
    setSubmitting(false);
    if (result.success) return navigate(from, { replace: true });
    setError(result.error || 'Não foi possível validar o código.');
  };

  return (
    <main className="min-h-screen bg-nexus-darker text-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-slate-950/80 p-8 shadow-2xl shadow-black/30">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-200">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-white">ERP OpenClaw</h1>
            <p className="text-sm text-white/50">Acesso restrito da operação</p>
          </div>
        </div>

        {mfaChallenge ? <form className="mt-8 space-y-5" onSubmit={handleMfaSubmit}>
          <p className="rounded-md border border-cyan-300/20 bg-cyan-400/10 px-4 py-3 text-sm text-cyan-100">Informe o código de 6 dígitos do autenticador ou um código de recuperação.</p>
          <label className="block"><span className="text-sm text-white/70">Código de autenticação</span><input type="text" autoComplete="one-time-code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-cyan-300/60" required autoFocus /></label>
          {error && <div className="rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">{error}</div>}
          <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70"><ShieldCheck className="h-4 w-4" />{submitting ? 'Validando...' : 'Confirmar acesso'}</button>
          <button type="button" onClick={() => { setMfaChallenge(''); setMfaCode(''); setError(''); }} className="w-full text-center text-sm text-white/60 transition hover:text-white">Voltar</button>
        </form> : <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-sm text-white/70">E-mail ou usuário</span>
            <input
              type="text"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-cyan-300/60 focus:bg-white/10"
              required
            />
          </label>

          <label className="block">
            <span className="text-sm text-white/70">Senha</span>
            <div className="relative mt-2">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-md border border-white/10 bg-white/5 px-10 py-3 text-white outline-none transition focus:border-cyan-300/60 focus:bg-white/10"
                required
              />
            </div>
          </label>

          {error && (
            <div className="rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <LogIn className="h-4 w-4" />
            {submitting ? 'Entrando...' : 'Entrar'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/esqueci-minha-senha')}
            className="w-full text-center text-sm text-cyan-200 transition hover:text-cyan-100"
          >
            Esqueci minha senha
          </button>
        </form>
        }
      </div>
    </main>
  );
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch('/erp/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) throw new Error('Não foi possível solicitar a recuperação agora.');
      setSent(true);
    } catch (err) {
      setError(err.message || 'Não foi possível solicitar a recuperação agora.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-nexus-darker text-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-slate-950/80 p-8 shadow-2xl shadow-black/30">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-200"><Mail className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-semibold text-white">Recuperar acesso</h1><p className="text-sm text-white/50">Enviaremos um link único para criar uma nova senha.</p></div>
        </div>
        {sent ? (
          <div className="mt-8 space-y-4">
            <p className="rounded-md border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">Se este e-mail estiver cadastrado, o link de recuperação foi enviado. Verifique também a caixa de spam.</p>
            <button onClick={() => navigate('/login')} className="w-full rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950">Voltar ao login</button>
          </div>
        ) : (
          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <label className="block"><span className="text-sm text-white/70">E-mail cadastrado</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-cyan-300/60 focus:bg-white/10" required /></label>
            {error && <div className="rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">{error}</div>}
            <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70"><Mail className="h-4 w-4" />{submitting ? 'Enviando...' : 'Enviar link de recuperação'}</button>
            <button type="button" onClick={() => navigate('/login')} className="w-full text-center text-sm text-white/60 transition hover:text-white">Voltar</button>
          </form>
        )}
      </div>
    </main>
  );
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const token = new URLSearchParams(window.location.search).get('token');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return setError('Este link de acesso é inválido. Solicite um novo convite.');
    if (password.length < 8) return setError('Crie uma senha com pelo menos 8 caracteres.');
    if (password !== confirmation) return setError('As senhas não coincidem.');
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch('/erp/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Não foi possível definir a senha.');
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Não foi possível definir a senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-nexus-darker text-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-slate-950/80 p-8 shadow-2xl shadow-black/30">
        <div className="flex items-center gap-3"><div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-200"><ShieldCheck className="h-6 w-6" /></div><div><h1 className="text-2xl font-semibold text-white">Ative seu acesso</h1><p className="text-sm text-white/50">Defina sua senha para entrar no ERP Nexus.</p></div></div>
        {success ? <div className="mt-8 space-y-4"><p className="rounded-md border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">Senha criada com sucesso. Agora você já pode acessar o ERP.</p><button onClick={() => navigate('/login')} className="w-full rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950">Ir para o login</button></div> : <form className="mt-8 space-y-5" onSubmit={handleSubmit}><label className="block"><span className="text-sm text-white/70">Nova senha</span><input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-cyan-300/60" required /></label><label className="block"><span className="text-sm text-white/70">Confirmar senha</span><input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-cyan-300/60" required /></label>{error && <div className="rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">{error}</div>}<button type="submit" disabled={submitting} className="w-full rounded-md bg-cyan-400 px-4 py-3 font-semibold text-slate-950 disabled:opacity-70">{submitting ? 'Salvando...' : 'Criar senha e acessar'}</button></form>}
      </div>
    </main>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router basename="/erp">
        <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-minha-senha" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            
            {/* Financeiro Routes */}
            <Route path="financeiro" element={<Guarded permission="financeiro:read"><FinanceiroDashboard /></Guarded>} />
            <Route path="financeiro/transacoes" element={<Guarded permission="financeiro:read"><FinanceiroTransacoes /></Guarded>} />
            <Route path="financeiro/fluxo-caixa" element={<Guarded permission="financeiro:read"><FinanceiroFluxoCaixa /></Guarded>} />
            <Route path="financeiro/dre" element={<Guarded permission="financeiro:read"><FinanceiroDRE /></Guarded>} />
            <Route path="financeiro/consolidado" element={<Guarded permission="financeiro:read"><FinanceiroConsolidado /></Guarded>} />
            <Route path="financeiro/contas-pagar" element={<Guarded permission="financeiro:read"><FinanceiroContasPagar /></Guarded>} />
            <Route path="financeiro/contas-receber" element={<Guarded permission="financeiro:read"><FinanceiroContasReceber /></Guarded>} />
            <Route path="financeiro/conciliacao" element={<Guarded permission="financeiro:read"><FinanceiroConciliacao /></Guarded>} />
            <Route path="financeiro/cartoes" element={<Guarded permission="financeiro:read"><FinanceiroCartoes /></Guarded>} />
            
            <Route path="imobiliaria" element={<Guarded permission="imobiliaria:read"><Imobiliaria /></Guarded>} />
            <Route path="construtora" element={<Guarded permission="construtora:read"><Construtora /></Guarded>} />
            <Route path="contabilidade" element={<Navigate to="/financeiro" replace />} />
            <Route path="rh" element={<Guarded permission="rh:read"><RH /></Guarded>} />
            <Route path="frota" element={<Guarded permission="frota:read"><Frota /></Guarded>} />
            <Route path="estoque" element={<Guarded permission="estoque:read"><Estoque /></Guarded>} />
            <Route path="juridico" element={<Guarded permission="juridico:read"><Juridico /></Guarded>} />
            <Route path="documentos" element={<Guarded permission="documentos:read"><DocumentosOCR /></Guarded>} />
            <Route path="revisoes" element={<Guarded permission="revisoes:read"><RevisoesPendentes /></Guarded>} />
            <Route path="chat" element={<Guarded permission="chat:read"><Chat /></Guarded>} />
            <Route path="marketing" element={<Guarded permission="marketing:read"><Marketing /></Guarded>} />
            <Route path="nexusteam" element={<Guarded permission="nexus_team:admin"><NexusTeam /></Guarded>} />
            <Route path="predictive" element={<Guarded permission="predictive:admin"><Predictive /></Guarded>} />
            <Route path="settings" element={<Guarded permission="users:read"><Settings /></Guarded>} />
          </Route>

          {/* Redirect unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthProvider>
      </Router>
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(15, 23, 42, 0.95)',
            color: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: 'rgba(15, 23, 42, 0.95)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: 'rgba(15, 23, 42, 0.95)',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
